import React, { useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMutation, useQuery } from '@apollo/client';
import { subYears } from 'date-fns';
import { IFormImperativeHandleProps } from '../../../../common/form';
import { Modal } from '../../../../common';
import { UPDATE_VISION_PROFILE } from '../../../../../graphql/profile/mutations';
import { GET_VISION_PROFILE } from '../../../../../graphql/profile/queries/get-vision-profile';
import { IVisionProfileData } from '../../../../../graphql/profile/models/get-vision-profile.models';
import { useCancellablePromise } from '../../../../../hooks/use-cancellable-promise/use-cancellable-promise';
import { useActionsInProgress } from '../../../../../graphql/preloader/actions/actions-in-progress';
import { toDate } from '../../../../../utils/date/to-date';
import { convertDateToString } from '../../../../../utils/date/convert-date-to-string';
import { promiseErrorCallbacks } from '../../../../../utils/promise/set-promise-error-callbacks';
import { yup } from '../../../../../services/yup';
import {
  BirthdayViewForm,
  TFormValues,
} from '../../vision-profile/vision-profile-views/birthday-view/birthday-view-form';
import { ReactComponent as BirthdayPieImage } from '../../../../../assets/images/kids/pie.svg';
import { useStyles as useKidsModalStyles } from '../../kids/create-kids-profile/birthday-modal/birthday-modal.styles';
import { v2025PP16783 } from '../../../../../constants/veeva';

interface ProfileBirthdayModalProps {
  dateOfBirth: Date | null;
  onClose?: () => void;
}

export const ProfileBirthdayModal = ({
  dateOfBirth: dateOfBirthCurrent,
  onClose,
  ...props
}: ProfileBirthdayModalProps): JSX.Element => {
  const ref = useRef<IFormImperativeHandleProps>(null);
  const intl = useIntl();
  const classes = useKidsModalStyles();
  const [value, setValue] = useState<Date | null>(dateOfBirthCurrent);

  const { updateQuery: updateBirthdayQuery } = useQuery<IVisionProfileData>(GET_VISION_PROFILE);
  const [updateBirthday] = useMutation(UPDATE_VISION_PROFILE);
  const { makeCancellablePromise, CancelledPromiseOnUnmountError } = useCancellablePromise();
  const { addActionInProgress, removeActionInProgress } = useActionsInProgress();

  const minDate = useMemo(() => toDate(new Date(1900, 0, 1)), [])!;
  const maxDateOfBirth = useMemo(() => toDate(subYears(new Date(), 15)), [])!;
  const defaultValues = useMemo(() => ({ dateOfBirth: dateOfBirthCurrent }), [dateOfBirthCurrent]);

  const validationSchema = useMemo(
    () =>
      yup.object({
        dateOfBirth: yup
          .date()
          .nullable()
          .required(intl.formatMessage({ id: 'common.errors.required.long' })),
      }),
    [intl],
  );

  const handleBirthdayChange = async ({ dateOfBirth }: TFormValues) => {
    if (!dateOfBirth) {
      return;
    }

    addActionInProgress();

    try {
      const {
        data: { patchVisionProfile },
      } = await makeCancellablePromise(
        updateBirthday({
          variables: {
            visionProfile: {
              dateOfBirth: convertDateToString(dateOfBirth),
            },
          },
        }),
      );

      updateBirthdayQuery((prevState) => ({
        ...prevState,
        visionProfile: {
          ...prevState?.visionProfile,
          ...patchVisionProfile,
        },
      }));

      onClose!();
    } catch (error) {
      if (error instanceof CancelledPromiseOnUnmountError) {
        return;
      }

      if (promiseErrorCallbacks.anyError) {
        promiseErrorCallbacks.anyError();
      }
    } finally {
      removeActionInProgress();
    }
  };

  const onChangeValue = (date: Date) => {
    setValue(date);
  };

  return (
    <Modal
      open={false}
      {...props}
      onClose={onClose}
      veevaNumber={v2025PP16783}
      content={
        <>
          <h2 className={classes.title}>
            {intl.formatMessage({ id: 'profile.birthday.modal.title' }, { noBreak: <>&nbsp;</> })}
          </h2>
          <div className={classes.pieImage} data-testid="birthday-pie-image">
            <BirthdayPieImage />
          </div>
          <BirthdayViewForm
            ref={ref}
            formProps={{
              onSubmit: handleBirthdayChange,
              defaultValues,
              validationSchema,
              reValidateMode: 'onChange',
            }}
            canUpdateDateOfBirth
            isAction
            isDisabled={!value}
            getValue={onChangeValue}
            maxDateOfBirth={maxDateOfBirth}
            minDate={minDate}
          />
        </>
      }
    />
  );
};
